import { Element } from '@candulabs/craft';
import { Button, CollectionIcon, EditIcon, Paragraph, theme } from 'canoe';
import { compose, connectWithHammock, ENDPOINTS } from 'hammock';
import React from 'react';
import styled from 'styled-components';

import CollectionListIcon from '!!react-svg-loader!canoe/icons/assets/CollectionList.svg';
import { CollectionCount, CollectionType } from '@candu-ai/collections/types';
import { useWorkspacePath } from 'commons/hooks';
import DropInContainer from 'craft/components/DropInContainer';
import IconWrapper from 'craft/components/IconWrapper/IconWrapper';
import { Collection } from 'craft/resolvers/Collection';
import { VIEW_DEFAULT_STYLES } from 'craft/resolvers/View';
import { COLLECTIONS_PATH } from 'dashboard/pages/Collections';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 383px);
  min-height: 250px;
  overflow: auto;
  margin-bottom: 8px;

  > * {
    margin-top: 8px;
  }
`;

const EditCollection = styled.a`
  height: 100%;
  padding: ${theme.spaces.s};
  position: absolute;
  top: 0;
  right: 0;
  display: none;
`;

const CollectionsRow = styled.div`
  display: flex;
  padding: ${theme.spaces.xs} ${theme.spaces.s};
  position: relative;
  width: 100%;

  > div {
    margin-left: ${theme.spaces.xs};
  }

  &:hover ${EditCollection} {
    display: flex;
  }
`;

const CollectionName = styled(Paragraph)`
  color: ${theme.colors.OnyxGrayL10};
  font-weight: ${theme.fontWeights.bold};
  font-size: ${theme.fonts.small.fontSize};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubHead = styled(Paragraph)`
  color: ${theme.colors.OnyxGrayL30};
  font-size: ${theme.fonts.small.fontSize};
`;

const CollectionRecordCount = styled(Paragraph)`
  color: ${theme.colors.OnyxGrayL10};
  font-size: ${theme.fonts.small.fontSize};
  margin: 0;
`;

const Icon = styled(DropInContainer)`
  display: flex;
  cursor: pointer;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: transparent;
  border: none;
  padding: 0;
  width: 100%;

  &:hover {
    border: none;

    ${CollectionsRow} {
      border-color: ${theme.colors.OnyxGrayL30};
    }
  }

  &:active {
    cursor: grabbing;

    ${CollectionsRow} {
      border-color: ${theme.colors.BrandPurple};
    }
  }
`;

const Link = styled.a`
  color: ${theme.colors.MangoOrangeD10};
  display: block;
  text-align: center;
`;

export const CollectionTabImpl = ({
  collections,
  collectionCounts,
}: {
  collections: CollectionType[];
  collectionCounts: CollectionCount[];
}) => {
  const workspacePath = useWorkspacePath();

  const handleCreateCollectionClick = () => {
    window.open(workspacePath(`${COLLECTIONS_PATH}/new`), '_blank');
  };

  return (
    <Container>
      <div css={{ marginBottom: '8px' }}>
        {collections.length ? (
          <IconWrapper
            id="collection"
            dropIn={
              <Element is={Collection} maxRecords={20} custom={{ css: VIEW_DEFAULT_STYLES }} />
            }
          >
            <CollectionListIcon />
            <span>List</span>
          </IconWrapper>
        ) : (
          <SubHead size="long">
            With Collections, you can enter data once, use it throughout your site, and when you
            update that data, it updates everywhere.
          </SubHead>
        )}
      </div>
      <Paragraph size="short">Your existing Collections</Paragraph>
      <Grid>
        {!collections.length && (
          <SubHead size="long">You don&apos;t have any Collections yet</SubHead>
        )}
        {collections.map((collection: CollectionType) => {
          const recordsCount =
            collectionCounts.find((collectionCount) => collectionCount.id === collection.id)
              ?.count || 0;
          return (
            <Icon
              key={collection.id}
              id="Collection"
              dropIn={
                <Element
                  is={Collection}
                  collectionId={collection.id}
                  maxRecords={20}
                  custom={{ css: VIEW_DEFAULT_STYLES }}
                />
              }
            >
              <CollectionsRow>
                <CollectionIcon color="OnyxGrayL10" />
                <div>
                  <CollectionName size="short">{collection.collectionName}</CollectionName>
                  <CollectionRecordCount>
                    {recordsCount} item{recordsCount > 1 && 's'}
                  </CollectionRecordCount>
                  <EditCollection
                    href={workspacePath(`${COLLECTIONS_PATH}/${collection.id}`)}
                    target="_blank"
                  >
                    <EditIcon color="OnyxGrayL20" />
                  </EditCollection>
                </div>
              </CollectionsRow>
            </Icon>
          );
        })}
      </Grid>
      <div>
        <Button
          appearance="outline"
          variant="orange"
          size="small"
          onClick={handleCreateCollectionClick}
          fullWidth
          mb="xs"
        >
          Create New Collection
        </Button>
        <Link href="https://docs.candu.ai">Learn about Collections</Link>
      </div>
    </Container>
  );
};

const withCollections = connectWithHammock({
  endpoint: ENDPOINTS.collections,
  resourceName: 'collections',
});

const withCounts = connectWithHammock({
  endpoint: ENDPOINTS.collectionCount,
  resourceName: 'collectionCounts',
});

export const CollectionTab = compose(withCollections, withCounts)(CollectionTabImpl);
